import React from 'react';
import Col from '../../Col';
import Row from '../../Row';
import ReactSelect from 'react-select';
import Checkbox from '../../Checkbox';
import { Multiselect } from 'multiselect-react-dropdown';
import Callout from '../../InformationCallout/Callout';
import { useBranding } from 'src/branding/BrandingContextProvider';
import {
  AnnouncementPropertyOption,
  EmptyScopeWarning,
} from '../hooks/useAnnouncementFormOrchestrator';

const selectStyles = {
  singleValue: (base: any) => ({ ...base, textAlign: 'left' as const }),
  placeholder: (base: any) => ({ ...base, textAlign: 'left' as const }),
  input: (base: any) => ({ ...base, textAlign: 'left' as const }),
  option: (base: any) => ({ ...base, textAlign: 'left' as const }),
};

interface PropertyAndScopeSectionProps {
  propertyOptions: AnnouncementPropertyOption[];
  propertyId: string | null;
  emptyScopeWarning: EmptyScopeWarning | null;
  plan: string;

  buildingOptions: any[];
  levelsOptions: any[];
  unitOptions: any[];

  selectedBuildings: any[];
  selectedFloorValues: any;
  selectedUnitValues: any[];

  buildingSelectAll: boolean;
  floorSelectAll: boolean;
  unitSelectAll: boolean;

  buildingRemountKey: number;
  floorRemountKey: number;
  unitRemountKey: number;

  onPropertyChange: (propertyId: string) => void;
  onPlanChange: (plan: string) => void;

  onBuildingsChange: (selectedList: any) => void;
  onFloorChange: (selectedList: any) => void;
  onUnitsChange: (selectedList: any) => void;

  onSelectAllBuildings: () => void;
  onSelectAllFloors: () => void;
  onSelectAllUnits: () => void;
}

const PropertyAndScopeSection = ({
  propertyOptions,
  propertyId,
  emptyScopeWarning,
  plan,
  buildingOptions,
  levelsOptions,
  unitOptions,
  selectedBuildings,
  selectedFloorValues,
  selectedUnitValues,
  buildingSelectAll,
  floorSelectAll,
  unitSelectAll,
  buildingRemountKey,
  floorRemountKey,
  unitRemountKey,
  onPropertyChange,
  onPlanChange,
  onBuildingsChange,
  onFloorChange,
  onUnitsChange,
  onSelectAllBuildings,
  onSelectAllFloors,
  onSelectAllUnits,
}: PropertyAndScopeSectionProps) => {
  const { branding } = useBranding();

  const suiteTerm: string = branding?.text?.suite || 'Suite';
  const suitePlural: string = `${suiteTerm}s`;

  const selectedPropertyOption: AnnouncementPropertyOption | null =
    propertyOptions.find(
      (option: AnnouncementPropertyOption) => option.value === propertyId
    ) ?? null;

  const scopePlaceholder = propertyId
    ? null
    : 'Please Select a Property First';
  const buildingPlaceholder = scopePlaceholder ?? 'Select Buildings';
  const floorPlaceholder = scopePlaceholder ?? 'Select Floor';
  const unitPlaceholder =
    unitOptions.length > 0
      ? `Select ${suitePlural}`
      : plan === 'floor'
        ? 'Please Select a Floor First'
        : 'Please Select a Building First';

  return (
    <Col xs="12" md="6">
      <div className="property-select">
        <label className="input__label">Property</label>
        <ReactSelect
          value={selectedPropertyOption}
          onChange={(selected: any) => onPropertyChange(selected?.value ?? '')}
          options={propertyOptions}
          placeholder="Select Property"
          styles={selectStyles}
        />
      </div>
      {emptyScopeWarning && (
        <div style={{ marginTop: '1.5rem'}}>
          <Callout
            variant="error"
            title={emptyScopeWarning.title}
            subtitle={emptyScopeWarning.subtitle}
          />
        </div>
      )}
      <Row>
        <Col xs="12" md="3">
          <label htmlFor="Building" className="plan-toggle">
            <Checkbox
              id="Buildings"
              name="plan"
              value={plan === 'buildings' ? plan : ''}
              checked={plan === 'buildings'}
              onChange={() => onPlanChange('buildings')}
            />
            <span>Buildings</span>
          </label>
        </Col>
        <Col xs="12" md="6">
          <label htmlFor="Floor" className="plan-toggle">
            <Checkbox
              id="Floor/Level"
              name="plan"
              value={plan === 'floor' ? plan : ''}
              checked={plan === 'floor'}
              onChange={() => onPlanChange('floor')}
            />
            <span>Floor/Level</span>
          </label>
        </Col>
      </Row>
      {plan === 'buildings' && (
        <div>
          <div className="scope-heading">
            <div className="reporting__heading">Buildings</div>
            <div className="picker__required"></div>
          </div>
          {buildingOptions.length !== 0 && (
            <div className="select-all-row">
              <input
                type="checkbox"
                checked={buildingSelectAll}
                onChange={() => onSelectAllBuildings()}
              />{' '}
              Select All
            </div>
          )}
          <Multiselect
            key={`building-${buildingRemountKey}`}
            id="building"
            options={buildingOptions}
            displayValue="name"
            selectedValues={selectedBuildings}
            placeholder={buildingPlaceholder}
            disable={buildingOptions.length === 0}
            onSelect={onBuildingsChange}
            onRemove={onBuildingsChange}
          />
        </div>
      )}
      {plan === 'floor' && (
        <div>
          <div className="scope-heading">
            <div className="reporting__heading">Floor/Level</div>
            <div className="picker__required" />
          </div>
          {levelsOptions.length !== 0 && (
            <div className="select-all-row">
              <input
                type="checkbox"
                checked={floorSelectAll}
                onChange={() => onSelectAllFloors()}
              />{' '}
              Select All
            </div>
          )}
          <Multiselect
            key={`level-${floorRemountKey}`}
            id="level"
            options={levelsOptions}
            displayValue="name"
            selectedValues={selectedFloorValues}
            placeholder={floorPlaceholder}
            disable={levelsOptions.length === 0}
            onSelect={onFloorChange}
            onRemove={onFloorChange}
          />
        </div>
      )}
      <br />
      <div className="unit-heading">
        <div className="reporting__heading">{`${suiteTerm}(s)`}</div>
        <div className="picker__required" />
      </div>
      {unitOptions.length !== 0 && (
        <div className="select-all-row">
          <input
            type="checkbox"
            checked={unitSelectAll}
            onChange={() => onSelectAllUnits()}
          />{' '}
          Select All
        </div>
      )}
      <Multiselect
        key={`unit-${unitRemountKey}`}
        options={unitOptions}
        displayValue="name"
        selectedValues={selectedUnitValues}
        placeholder={unitPlaceholder}
        disable={unitOptions.length === 0}
        onSelect={onUnitsChange}
        onRemove={onUnitsChange}
      />
      <br />
    </Col>
  );
};

export default PropertyAndScopeSection;
